import {
  Alert,
  Button,
  Card,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import FormInputCheckBox from "../../Inputs/FormInputCheckBox";
import FormInputComboBox from "../../Inputs/FormInputComboBox";
import FormInputDatePicker from "../../Inputs/FormInputDatePicker";
import FormInputDateTimePicker from "../../Inputs/FormInputDateTimePicker";
import FormInputMSPerson from "../../Inputs/FormInputMSPerson";
import FormInputText from "../../Inputs/FormInputText";
import FormInputRTE from "../../Inputs/FormInputRTE";
import style from "./EditWorkshop.module.css";
import {
  unstable_usePrompt,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { DateTime } from "luxon";
import {
  copySelectedWorkshop,
  saveNewWorkshop,
  saveWorkshop,
  setFormSubmitState,
  setSelectedWorkshop,
  submitStates,
} from "../../../state/workshopSlice";
import useGetCurrentUser from "../../../hooks/useGetCurrentUser";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClassIcon from "@mui/icons-material/Class";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Person,
  PersonCardInteraction,
  PersonViewType,
} from "@microsoft/mgt-react";
import FormInputTagField from "../../Inputs/FormInputTagField";
import useIsAdmin from "../../../hooks/useIsAdmin";

const startingFormValues = {
  title: "",
  category: null,
  breakfast: false,
  lunch: false,
  snack: false,
  dinner: false,
  internalPresenter: null,
  externalPresenter: "",
  organizer: null,
  capacity: "",
  site: null,
  description: "",
  locationDescription: "",
  externalSite: "",
  virtual: false,
  focusArea: "",
  targetAudience: "",
  workshopStart: "",
  workshopEnd: "",
  registrationStartDate: "",
  registrationEndDate: "",
  moreInformationUrl: "",
  notifyAdmin: false,
  tags: "",
  daysBeforeToRemind: 0,
};

const EditWorkshop = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useGetCurrentUser();
  const isAdmin = useIsAdmin();
  const [newForm, setNewForm] = useState(true);
  const [formStart, setFormStart] = useState(startingFormValues);
  const categories = useSelector((state) => state.workshops.categories);
  const sites = useSelector((state) => state.workshops.sites);
  const workshop = useSelector((state) => state.workshops.selectedWorkshop);
  const { formSubmitState } = useSelector((state) => state.workshops.status);

  //set up the form tracking using react-hook-form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    getFieldState,
    formState: { errors, isValid, isDirty, isSubmitted },
  } = useForm({
    defaultValues: formStart,
    criteriaMode: "all",
    mode: "onChange",
  });

  const setFormValuesFromWorkshop = (w) => {
    reset({
      title: w.title,
      category: {
        id: w.category.categoryId,
        label: w.category.name,
      },
      breakfast: w.breakfast,
      lunch: w.lunch,
      snack: w.snack,
      dinner: w.dinner,
      internalPresenter: w.presenters.map((p) => p.staff?.mail),
      externalPresenter: w.externalPresenter,
      organizer: [w.organizer.mail],
      capacity: w.capacity,
      site: w.site && {
        id: w.site.siteId,
        label: w.site.siteName,
      },
      description: w.description,
      locationDescription: w.locationDescription,
      externalSite: w.externalSite,
      virtual: w.virtual,
      focusArea: w.focusArea,
      targetAudience: w.targetAudience,
      workshopStart: DateTime.fromISO(w.startDatetime),
      workshopEnd: DateTime.fromISO(w.endDatetime),
      registrationStartDate: DateTime.fromISO(w.registrationStartDate),
      registrationEndDate: DateTime.fromISO(w.registrationEndDate),
      moreInformationUrl: w.moreInformationUrl,
      notifyAdmin: false,
      tags: w.tags,
      daysBeforeToRemind: w.daysBeforeToRemind,
    });
  };

  // unstable_usePrompt(
  //   "Hello from usePrompt -- Are you sure you want to leave?",
  //   isDirty
  // );

  //use the id from the url to monitor which workshop to display
  const { id } = useParams();
  //this effect is processed when id parameter changes
  //or workshop (selectedWorkshop) changes
  //or url parameters change
  //or the form submit state changes
  useEffect(() => {
    if (formSubmitState == submitStates.saveSuccessful) {
      //this update was caused by the form saving
      //if it was a new or workshop copy redirect to the new workshop
      if (newForm || searchParams.get("copy")) {
        //remove the copy parameter (doesn't exist if new form)
        searchParams.delete("copy");
        //when a new workshop has been successfully created update the url to use the new workshop id
        let id = workshop.workshopId;
        dispatch(setSelectedWorkshop(null));
        navigate(`/edit/${id}`);
      }
      dispatch(setFormSubmitState(submitStates.idle));
      return;
    }

    if (id && workshop) {
      //when we have a selected workshop and a workshop id parameter from the url
      //we are in edit mode, set the default values for the form
      setFormValuesFromWorkshop(workshop);
      setNewForm(false);
      return;
    }

    if (searchParams.get("copy") && workshop) {
      //copy existing workshop
      const workshopCopy = {
        ...workshop,
        workshopId: null,
        creatorStaffId: null,
        createdDatetime: null,
        creator: null,
      };

      setFormValuesFromWorkshop(workshopCopy);
      dispatch(setSelectedWorkshop(null));
      setNewForm(true);
      return;
    }

    if (id === undefined && !searchParams.get("copy")) {
      //this is a brand new form
      reset(startingFormValues);
      setNewForm(true);
    }
  }, [id, workshop, searchParams, formSubmitState]);

  const onSubmit = (data) => {
    const updatedWorkshop = {
      ...data,
      workshopId: workshop?.workshopId || null,
      categoryId: data.category.id,
      siteId: data.site?.id,
      creatorMail: user.mail,
      organizerMail: data.organizer[0],
      presenterMails: data.internalPresenter,
      registrationStartDate: data.registrationStartDate.toISO(),
      registrationEndDate: data.registrationEndDate.toISO(),
      startDatetime: data.workshopStart.toISO(),
      endDateTime: data.workshopEnd.toISO(),
    };

    updatedWorkshop.workshopId
      ? dispatch(saveWorkshop(updatedWorkshop))
      : dispatch(saveNewWorkshop(updatedWorkshop));
  };

  const onError = (data) => {
    console.log("errors: ", data);
  };

  //TODO: delete this:
  const doDebug = () => {
    console.log("values: ", getValues());
    console.log("errors: ", errors);
  };

  //CUSTOM VALIDATION
  const onlyOne = () => {
    //this is the validation function for ensuring only one location is filled
    //XOR for the three fields: site, externalSite, virtual
    if (
      (getValues("site") !== null && getValues("externalSite") !== "") ||
      (getValues("site") !== null && getValues("virtual") === true) ||
      (getValues("externalSite") !== "" && getValues("virtual") === true)
    )
      return false;

    return true;
  };

  return (
    <Box sx={{ background: "lightgrey" }}>
      {isAdmin && (
        <form className={style.form}>
          <Grid2 container spacing={1}>
            <Grid2 xs={12}>
              <Paper
                elevation={3}
                sx={{
                  backgroundColor: "primary.light",
                  color: "specialHeadingText.main",
                  padding: "1rem 1rem 0.5rem 1rem",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                      {newForm ? "Create new workshop" : workshop?.title}
                    </Typography>
                    <Typography variant="h6" sx={{ marginTop: "0.2rem" }}>
                      {!newForm && "Edit workshop"}
                    </Typography>
                  </Box>
                  {!newForm && (
                    <Box>
                      <Typography variant="body2">
                        Workshop ID: {workshop?.workshopId}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {workshop && !newForm && (
                  <Box
                    sx={{
                      display: "flex",
                      marginTop: "0.5rem",
                      backgroundColor: "white",
                      borderRadius: "6px",
                      padding: "0.25rem 1rem",
                      gap: "0.5rem",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2">Created by:</Typography>
                      <Person
                        view={PersonViewType.oneline}
                        personQuery={workshop.creator.mail}
                        personCardInteraction={PersonCardInteraction.hover}
                        avatarSize="small"
                      ></Person>
                    </Box>
                    <Box>
                      <Typography variant="body2">Created on:</Typography>
                      <Typography variant="body2">
                        {DateTime.fromISO(
                          workshop.createdDatetime
                        ).toLocaleString(DateTime.DATETIME_MED)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid2>
            <Grid2 xs={12}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <ClassIcon fontSize="inherit" color="secondary" /> Workshop
                  Information
                </Box>
                <FormInputText
                  name="title"
                  label="Workshop Title"
                  required
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter a title for this workshop",
                    },
                  }}
                  control={control}
                  error={errors.title?.message}
                />
                <FormInputComboBox
                  name="category"
                  label="Category"
                  control={control}
                  required
                  rules={{
                    required: {
                      value: true,
                      message: "Please choose a category",
                    },
                  }}
                  error={errors.category?.message}
                  options={categories.map((category) => {
                    return { id: category.categoryId, label: category.name };
                  })}
                />
                <FormInputText
                  name="capacity"
                  label="Capacity"
                  type="number"
                  required
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter the number of seats",
                    },
                    min: {
                      value: 1,
                      message: "A workshop must have at least 1 seat",
                    },
                    valueAsNumber: true,
                    validate: {
                      hasRoom: (capacity) =>
                        workshop === null ||
                        capacity >= workshop.seats?.numberRegistered ||
                        "The capacity cannot be lower than number of currently registered people",
                    },
                  }}
                  control={control}
                  error={errors.capacity?.message}
                  width={40}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={12} lg={6}>
              <Card elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <RecordVoiceOverIcon fontSize="inherit" color="secondary" />{" "}
                  Presenter{" "}
                  <Tooltip title="Select one or more district staff members or enter the name of an external presenter">
                    <InfoOutlinedIcon fontSize="inherit" />
                  </Tooltip>
                </Box>
                <FormInputMSPerson
                  name="internalPresenter"
                  label="District Presenters"
                  control={control}
                  error={errors.internalPresenter?.message}
                  allowMultiple
                  rules={{
                    validate: {
                      atLeastOne: (v) =>
                        //getFieldState("internalPresenter").isDirty ||
                        getValues("internalPresenter")?.length > 0 ||
                        getValues("externalPresenter") !== "" ||
                        "Please enter a district or external presenter",
                      onlyOne: (v) =>
                        !(
                          getValues("internalPresenter")?.length > 0 &&
                          getValues("externalPresenter") !== ""
                        ) || "Please enter only district or external presenter",
                    },
                  }}
                  onChange={() => {
                    trigger(["internalPresenter", "externalPresenter"]);
                  }}
                />
                <FormInputText
                  name="externalPresenter"
                  label="External Presenter"
                  control={control}
                  rules={{
                    validate: {
                      atLeastOne: (v) =>
                        //getFieldState("externalPresenter").isDirty ||
                        getValues("internalPresenter")?.length > 0 ||
                        getValues("externalPresenter") !== "" ||
                        "Please enter a district or external presenter",
                      onlyOne: (v) =>
                        !(
                          getValues("internalPresenter")?.length > 0 &&
                          getValues("externalPresenter") !== ""
                        ) || "Please enter only district or external presenter",
                    },
                  }}
                  onChange={() => {
                    trigger(["internalPresenter", "externalPresenter"]);
                  }}
                  error={errors.externalPresenter?.message}
                />
              </Card>
            </Grid2>
            <Grid2 xs={12} md={12} lg={6}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <EmojiPeopleIcon fontSize="inherit" color="secondary" />{" "}
                  Organizer{" "}
                  <Tooltip title="Select the staff member who is organizing this workshop">
                    <InfoOutlinedIcon fontSize="inherit" />
                  </Tooltip>
                </Box>
                <FormInputMSPerson
                  name="organizer"
                  label="Organizer"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please select a workshop organizer",
                    },
                  }}
                  error={errors.organizer?.message}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={12} lg={6}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <PermContactCalendarIcon
                    fontSize="inherit"
                    color="secondary"
                  />{" "}
                  Registration{" "}
                  <Tooltip title="Registration for the workshop will be open between these dates. Dates are in MM/DD/YYYY format">
                    <InfoOutlinedIcon fontSize="inherit" />
                  </Tooltip>
                </Box>
                <FormInputDatePicker
                  name="registrationStartDate"
                  label="Registration Start Date"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "Select the date when registration opens",
                    },
                    validate: {
                      startBeforeEnd: (v) =>
                        getValues("registrationStartDate").valueOf() <=
                          getValues("registrationEndDate").valueOf() ||
                        "Start date must be before end date",
                    },
                  }}
                  onChange={() => {
                    trigger(["registrationStartDate", "registrationEndDate"]);
                  }}
                  error={errors.registrationStartDate?.message}
                />
                <FormInputDatePicker
                  name="registrationEndDate"
                  label="Registration End Date"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "Select the last day of registration",
                    },
                    validate: {
                      startBeforeEnd: (v) =>
                        getValues("registrationStartDate").valueOf() <=
                          getValues("registrationEndDate").valueOf() ||
                        "End date must be after start date",
                    },
                  }}
                  onChange={() => {
                    trigger(["registrationStartDate", "registrationEndDate"]);
                  }}
                  error={errors.registrationEndDate?.message}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={12} lg={6}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <EventAvailableIcon fontSize="inherit" color="secondary" />{" "}
                  Workshop Dates{" "}
                  <Tooltip title="The workshop starts and ends at these dates/times. Fields are in MM/DD/YYYY HH:MM format. Use the copy button to copy the start date to the end date field">
                    <InfoOutlinedIcon fontSize="inherit" />
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <FormInputDateTimePicker
                    name="workshopStart"
                    label="Workshop Start"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: "Set the start date/time of the workshop",
                      },
                      validate: {
                        startBeforeEnd: (v) =>
                          getValues("workshopStart").valueOf() <=
                            getValues("workshopEnd").valueOf() ||
                          "Start must be before end",
                      },
                    }}
                    onChange={() => {
                      trigger(["workshopStart", "workshopEnd"]);
                    }}
                    error={errors.workshopStart?.message}
                  />
                  <Tooltip title="Copy start date to end date field">
                    <IconButton
                      color="secondary"
                      aria-label="copy start date to end date"
                      onClick={() => {
                        setValue("workshopEnd", getValues("workshopStart"));
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormInputDateTimePicker
                  name="workshopEnd"
                  label="Workshop End"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "Set the end date/time of the workshop",
                    },
                    validate: {
                      startBeforeEnd: (v) =>
                        getValues("workshopStart").valueOf() <=
                          getValues("workshopEnd").valueOf() ||
                        "End must be after start",
                    },
                  }}
                  onChange={() => {
                    trigger(["workshopStart", "workshopEnd"]);
                  }}
                  error={errors.workshopEnd?.message}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <LocationOnIcon fontSize="inherit" color="secondary" />{" "}
                  Location
                </Box>
                <FormInputComboBox
                  name="site"
                  label="District Location"
                  control={control}
                  rules={{
                    validate: {
                      atLeastOne: (v) =>
                        getValues("site") !== null ||
                        getValues("externalSite") !== "" ||
                        getValues("virtual") === true ||
                        "Please select a location or virtual",
                      onlyOne: (v) =>
                        onlyOne() ||
                        "Please choose only one location or virtual",
                    },
                  }}
                  onChange={() => {
                    trigger(["site", "externalSite", "virtual"]);
                  }}
                  error={errors.site?.message}
                  options={sites.map((site) => {
                    return { id: site.siteId, label: site.siteName };
                  })}
                />
                <FormInputText
                  name="externalSite"
                  label="External Location"
                  control={control}
                  error={errors.externalSite?.message}
                  rules={{
                    validate: {
                      atLeastOne: (v) =>
                        getValues("site") !== null ||
                        getValues("externalSite") !== "" ||
                        getValues("virtual") === true ||
                        "Please select a location or virtual",
                      onlyOne: (v) =>
                        onlyOne() ||
                        "Please choose only one location or virtual",
                    },
                  }}
                  onChange={() => {
                    trigger(["site", "externalSite", "virtual"]);
                  }}
                />
                <FormInputCheckBox
                  name="virtual"
                  label="Virtual Workshop"
                  control={control}
                  rules={{
                    validate: {
                      atLeastOne: (v) =>
                        getValues("site") !== null ||
                        getValues("externalSite") !== "" ||
                        getValues("virtual") === true ||
                        "Please select a location or virtual",
                      onlyOne: (v) =>
                        onlyOne() ||
                        "Please choose only one location or virtual",
                    },
                  }}
                  onChange={() => {
                    trigger(["site", "externalSite", "virtual"]);
                  }}
                  error={errors.virtual?.message}
                />
                <FormInputText
                  name="locationDescription"
                  label="Address / Location Details"
                  control={control}
                  error={errors.locationDescription?.message}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={4}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <RestaurantIcon fontSize="inherit" color="secondary" /> Meals
                </Box>
                <FormInputCheckBox
                  name="breakfast"
                  label="Breakfast provided"
                  control={control}
                />
                <FormInputCheckBox
                  name="lunch"
                  label="Lunch provided"
                  control={control}
                />
                <FormInputCheckBox
                  name="snack"
                  label="Snack provided"
                  control={control}
                />
                <FormInputCheckBox
                  name="dinner"
                  label="Dinner provided"
                  control={control}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={8}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <ImportContactsIcon fontSize="inherit" color="secondary" />{" "}
                  Other Information
                </Box>
                <FormInputText
                  name="targetAudience"
                  label="Target Audience"
                  control={control}
                  error={errors.targetAudience?.message}
                />
                <FormInputText
                  name="focusArea"
                  label="Focus Area"
                  control={control}
                  error={errors.focusArea?.message}
                />
                <FormInputText
                  name="moreInformationUrl"
                  label="URL for More Information"
                  control={control}
                  error={errors.moreInformationUrl?.message}
                />
                <FormInputTagField name="tags" label="Tags" control={control} />
                <FormInputText
                  name="daysBeforeToRemind"
                  label="Days before to remind"
                  helpText="The number of days before the workshop starts the system will automatically send out a reminder. Set to 0 for no reminder."
                  type="number"
                  rules={{
                    required: {
                      value: true,
                      message:
                        "Please enter a number of days or use 0 for no reminder",
                    },
                    min: {
                      value: 0,
                      message: "Must be 0 or positive number of days",
                    },
                    valueAsNumber: true,
                  }}
                  control={control}
                  error={errors.daysBeforeToRemind?.message}
                  width={60}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12}>
              <Paper elevation={2} className={style.section}>
                <Box className={style.sectionTitle}>
                  <AssignmentIcon fontSize="inherit" color="secondary" />{" "}
                  Workshop Description
                </Box>
                <FormInputRTE
                  name="description"
                  label="Workshop Description"
                  control={control}
                />
              </Paper>
            </Grid2>
            <Grid2 xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                {isSubmitted && !isValid && (
                  <Alert severity="warning" variant="filled">
                    Fix form validation errors before saving
                  </Alert>
                )}
                {/* <Button onClick={() => doDebug()}>Debug</Button> */}
                <Button
                  disabled={!isDirty}
                  startIcon={<RestartAltIcon />}
                  onClick={() => reset()}
                  variant={"outlined"}
                >
                  Reset Form
                </Button>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={(e) =>
                    handleSubmit(
                      onSubmit,
                      onError
                    )(e).catch((e) => {
                      console.log("e", e);
                    })
                  }
                >
                  Save Workshop
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        </form>
      )}
    </Box>
  );
};

export default EditWorkshop;
